<script setup>
import { Menu, MenuButton, MenuItem, MenuItems, Portal } from '@headlessui/vue'
import { ChevronDownIcon } from '@heroicons/vue/solid'
import { usePopper } from "@/utils/use-popper";
import {Inertia} from "@inertiajs/inertia";
import {computed, ref} from "vue";
import {Link} from "@inertiajs/inertia-vue3";

const props = defineProps({
  quotesheet: Object,
  lead_id: Number,
  hideApplication: {
      type: Boolean,
      default: false,
  },
  hideOptions: {
      type: Boolean,
      default: false,
  },
  legacyId: String,
})

function confirmDelete() {
  Swal.fire({
    title: 'Are you sure?',
    text: 'You will not be able to undo this.',
    icon: 'warning',
    confirmButtonText: "Yes, I'm sure.",
    showCancelButton: true,
    confirmButtonColor: '#B91C1C',
    cancelButtonColor: '#899CC1'
  }).then((result) => {
    if (result.isConfirmed) {
      deleteQuotesheet();
    }
  })
}


const deleting = ref(false);
function deleteQuotesheet() {
  if (deleting.value) return;
  deleting.value = true;
  Inertia.delete(route('lead.quotesheets.destroy', [props.lead_id, props.quotesheet.id]), {
    preserveScroll: true,
    onFinish: () => {
      deleting.value = false;
    }
  });
}

// The Emailed / Texted columns can push the table past its container, so the
// Options column is pinned to the right edge of the horizontal scroller. A
// sticky cell needs its own background or the rows show through it.
const rowBackgroundClass = computed(() => props.quotesheet.application_started ? 'bg-yellow-100' : 'bg-white');

let [trigger, container] = usePopper({
  placement: 'bottom-start',
  strategy: 'fixed',
  modifiers: [
    {
      name: 'offset',
      options: {
        offset: [0, 5],
      },
    },
  ],
})
</script>

<template>
  <tr :class="[rowBackgroundClass, props.quotesheet.application_started ? '' : 'hover:bg-gray-50', 'relative group']">
    <td class="whitespace-nowrap py-3.5 pl-4 pr-3 text-sm text-gray-900 first-letter:uppercase">{{ props.quotesheet.formatted_created_at ?? '—' }}</td>
    <td class="whitespace-nowrap px-2 py-3.5  text-sm font-medium text-gray-900">{{ props.quotesheet.prepared_for ?? '—' }}</td>
    <td v-for="channel in ['email_sent', 'text_sent']" :key="channel" class="whitespace-nowrap px-2 py-3.5 text-sm text-gray-900">
      <template v-if="props.quotesheet[channel]?.last">
        <div>{{ props.quotesheet[channel].last }}</div>
        <div v-if="props.quotesheet[channel].first && props.quotesheet[channel].first !== props.quotesheet[channel].last" class="text-xs text-gray-500">
          First: {{ props.quotesheet[channel].first }}
        </div>
      </template>
      <span v-else-if="props.quotesheet[channel]?.pending" class="text-gray-500 italic">Sending…</span>
      <template v-else>—</template>
    </td>
    <td v-if="!hideApplication" class="whitespace-nowrap px-2 py-3.5  text-sm text-gray-900">{{ props.quotesheet.status }}</td>
    <td v-if="legacyId" class="whitespace-nowrap px-2 py-3.5  text-sm text-gray-900"><a :href="`http://topbrokercrm.com/go/qsx.asp?key=${legacyId}`" target="_blank" class="text-blue-700">Open</a> </td>
    <td v-if="!hideOptions"
        :class="[rowBackgroundClass, props.quotesheet.application_started ? '' : 'group-hover:bg-gray-50', 'sticky right-0 whitespace-nowrap border-l border-gray-200 px-2 py-3.5 text-sm text-gray-900']">
      <Menu as="div" class="relative inline-block text-left">
        <div>
          <MenuButton ref="trigger" class="inline-flex justify-center w-full rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-100 focus:ring-tb-blue">
            Options
            <ChevronDownIcon class="-mr-1 ml-2 h-5 w-5" aria-hidden="true" />
          </MenuButton>
        </div>

        <Portal>
          <MenuItems ref="container" class="origin-top-right absolute right-0 w-56 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 focus:outline-none">
            <div class="py-1">
              <MenuItem v-slot="{ active }">
                <Link :href="route('lead.quotesheets.show', [props.lead_id, props.quotesheet.id])" :class="[active ? 'bg-gray-100 text-gray-900' : 'text-gray-700', 'block px-4 py-2 text-sm']">Modify</Link>
              </MenuItem>
              <MenuItem v-slot="{ active }">
                <a :href="route('lead.quotesheets.preview', props.quotesheet.uuid)" target="_blank" :class="[active ? 'bg-gray-100 text-gray-900' : 'text-gray-700', 'block px-4 py-2 text-sm']">Preview</a>
              </MenuItem>
              <MenuItem v-slot="{ active }">
                <Link :href="route('lead.quotesheets.send.create', [props.lead_id, props.quotesheet.id])" :class="[active ? 'bg-gray-100 text-gray-900' : 'text-gray-700', 'block px-4 py-2 text-sm']">Send</Link>
              </MenuItem>
              <MenuItem v-slot="{ active }">
                <a @click.prevent="confirmDelete" href="#" :class="[active ? 'bg-gray-100 text-gray-900' : 'text-gray-700', 'block px-4 py-2 text-sm']">Delete</a>
              </MenuItem>
            </div>
          </MenuItems>
        </Portal>
      </Menu>
    </td>
  </tr>
</template>

<style scoped></style>
